import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/DateInput";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { absencesApi, ABSENCE_TYPES, LEAVE_TYPES } from "@/api";
import { useLang } from "@/i18n";

const today = () => new Date().toISOString().slice(0, 10);
const EMPTY = { employee_id: "", type: "annual_leave", date: today(), end_date: today(), hours: 8, notes: "" };

const Field = ({ label, children, className = "" }) => (
  <div className={`space-y-1.5 ${className}`}>
    <Label className="text-xs uppercase tracking-wider text-slate-500">{label}</Label>
    {children}
  </div>
);

export const AbsenceDialog = ({ open, onOpenChange, absence, employees, onSaved }) => {
  const { t } = useLang();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const isLeave = LEAVE_TYPES.includes(form.type);

  useEffect(() => {
    if (open) setForm(absence ? { ...EMPTY, ...absence, end_date: absence.end_date || absence.date } : EMPTY);
  }, [open, absence]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.employee_id) return toast.error(`${t("employee")}: ${t("required")}`);
    if (!isLeave && !(Number(form.hours) > 0)) return toast.error(`${t("hours")}: ${t("required")}`);
    setSaving(true);
    try {
      const payload = { employee_id: form.employee_id, type: form.type, date: form.date, notes: form.notes,
        end_date: isLeave ? form.end_date || form.date : null, hours: isLeave ? 0 : Number(form.hours) };
      await (absence ? absencesApi.update(absence.id, payload) : absencesApi.create(payload));
      toast.success(t("absence_saved"));
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.status === 400 ? t("no_working_days") : t("error"));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="absence-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{absence ? t("edit_absence") : t("new_absence")}</DialogTitle>
          <DialogDescription className="sr-only">{t("absences")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-4">
          <Field label={t("employee")} className="col-span-2">
            <Select value={form.employee_id} onValueChange={set("employee_id")}>
              <SelectTrigger data-testid="absence-employee-select"><SelectValue placeholder={t("select_employee")} /></SelectTrigger>
              <SelectContent>{employees.filter((e) => e.active || e.id === form.employee_id).map((e) => <SelectItem key={e.id} value={e.id}>{e.last_name} {e.first_name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label={t("type")} className="col-span-2">
            <Select value={form.type} onValueChange={set("type")}>
              <SelectTrigger data-testid="absence-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{ABSENCE_TYPES.map((k) => <SelectItem key={k} value={k}>{t(`abs_${k}`)}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label={isLeave ? t("start_date") : t("date")}>
            <DateInput value={form.date} onChange={set("date")} className="font-mono" data-testid="absence-date-input" />
          </Field>
          {isLeave ? (
            <Field label={t("end_date")}>
              <DateInput value={form.end_date} min={form.date} onChange={set("end_date")} className="font-mono" data-testid="absence-end-date-input" />
            </Field>
          ) : (
            <Field label={t("hours")}>
              <Input type="number" step="0.5" min="0.5" value={form.hours} onChange={set("hours")} className="font-mono" data-testid="absence-hours-input" />
            </Field>
          )}
          <Field label={t("notes")} className="col-span-2">
            <Input value={form.notes} onChange={set("notes")} data-testid="absence-notes-input" />
          </Field>
          <DialogFooter className="col-span-2 mt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="absence-cancel-button">{t("cancel")}</Button>
            <Button type="submit" disabled={saving} className="bg-blue-800 hover:bg-blue-700" data-testid="absence-submit-button">{t("save")}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
